import Loader from "@components/Loader";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

type LoadingContextType = () => () => void;

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider = ({ children }: PropsWithChildren) => {
  const ref = useRef<string[]>([]);
  const [loading, setLoading] = useState(false);

  const startLoading = useCallback(() => {
    const uid = Math.random().toString(16).slice(2);
    ref.current.push(uid);
    setLoading(true);
    return () => {
      ref.current = ref.current.filter((f) => f !== uid);
      if (ref.current.length === 0) {
        setLoading(false);
      }
    };
  }, []);

  return (
    <LoadingContext.Provider value={startLoading}>
      {children}
      {loading && <Loader />}
    </LoadingContext.Provider>
  );
};

const useLoading = (loading: boolean) => {
  const context = useContext(LoadingContext);
  const end = useRef<(() => void) | undefined>();
  if (!context) {
    throw new Error("Cant use useLoading hook out of LoadingProvider");
  }

  useEffect(() => {
    if (loading) {
      end.current = context();
    } else {
      end.current?.();
      end.current = undefined;
    }
    return () => {
      end.current?.();
      end.current = undefined;
    };
  }, [context, loading]);
};

export default useLoading;
